import Header from "../../../shared/app-shell/Header";
import Sidebar from "../../../shared/app-shell/Sidebar";
import { themeAtom } from "../../../shared/atoms";
import HullPaintsDataTable from "../components/HullPaintsDataTable";
import HullPaintsExternalActionModal from "../components/HullPaintsExternalActionModal";
import HullPaintsFilterPanel from "../components/HullPaintsFilterPanel";
import type { HullPaintsExternalAction, HullPaintsFilters, HullPaintsTableMode } from "../models/hull-paints.models";
import { useHullPaintsDashboard } from "../state/useHullPaintsDashboard";
import {
  EMPTY_HULL_PAINTS_FILTERS,
  filterHullPaintsPaints,
  filterHullPaintsVessels,
} from "../utils/hull-paints-filter.utils";
import { ArrowPathIcon, BookOpenIcon, ClockIcon, EnvelopeIcon, EyeIcon, SwatchIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

const panelClass = "rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800";

// Recreates the PBIP's single-page vessel and paint-library experience.
// Snowflake data is live when mock mode is off; SharePoint and Power Automate actions remain explicit external gaps.
const HullPaintsInfoActiveVessels = () => {
  const theme = useAtomValue(themeAtom);
  const { dashboard, error, loading, mockEnabled, refresh } = useHullPaintsDashboard();
  const [filters, setFilters] = useState<HullPaintsFilters>(EMPTY_HULL_PAINTS_FILTERS);
  const [tableMode, setTableMode] = useState<HullPaintsTableMode>("vessels");
  const [selectedImos, setSelectedImos] = useState<Set<string>>(new Set());
  const [externalAction, setExternalAction] = useState<HullPaintsExternalAction | null>(null);

  const filteredVessels = useMemo(
    () => filterHullPaintsVessels(dashboard?.vessels ?? [], filters),
    [dashboard?.vessels, filters],
  );
  const filteredPaints = useMemo(
    () => filterHullPaintsPaints(dashboard?.paints ?? [], filteredVessels),
    [dashboard?.paints, filteredVessels],
  );
  const selectedVessels = useMemo(
    () => filteredVessels.filter((vessel) => selectedImos.has(vessel.imoNumber)),
    [filteredVessels, selectedImos],
  );
  const requestVessels = selectedVessels.length > 0 ? selectedVessels : filteredVessels;

  useEffect(() => {
    setFilters(EMPTY_HULL_PAINTS_FILTERS);
    setTableMode("vessels");
    setSelectedImos(new Set());
    setExternalAction(null);
  }, [mockEnabled]);

  const toggleVessel = (imoNumber: string) => {
    setSelectedImos((current) => {
      const next = new Set(current);
      next.has(imoNumber) ? next.delete(imoNumber) : next.add(imoNumber);

      return next;
    });
  };

  const openSendRequest = () => {
    setTableMode("vessels");
    setExternalAction("send-request");
  };

  const closeExternalAction = () => {
    if (externalAction === "send-request") {
      setTableMode("vessels");
    }
    setExternalAction(null);
  };

  const lastRefresh = dashboard?.lastRefresh ? new Date(dashboard.lastRefresh).toLocaleString() : "Not loaded";
  const visibleCount = tableMode === "vessels" ? filteredVessels.length : filteredPaints.length;

  return (
    <div className="flex h-screen" data-test="hull-paints-page">
      <Sidebar />
      <div
        className={twMerge(
          "flex min-w-0 flex-1 flex-col overflow-hidden",
          theme.home.tailwind.backgound,
          theme.home.tailwind.textDefault,
        )}
      >
        <Header />
        <main className="flex-1 overflow-y-auto px-4 pb-6 pt-3 sm:px-6">
          <div className="mx-auto max-w-[1800px]">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-20 text-green dark:bg-teal-900/50">
                  <SwatchIcon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-green">Fleet maintenance</p>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 sm:text-2xl">
                    Hull Paints Information Running Vessels
                  </h1>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={twMerge(
                    "rounded-full px-3 py-1 text-xs font-semibold",
                    mockEnabled ? "bg-amber-100 text-amber-800" : "bg-primary-10 text-green",
                  )}
                  data-test="hull-paints-source-badge"
                >
                  {mockEnabled ? "Modeled mock data" : "Snowflake API"}
                </span>
                <button
                  type="button"
                  onClick={refresh}
                  className="rounded-lg border border-gray-300 p-2 hover:border-green dark:border-gray-600"
                  aria-label="Refresh Hull Paints"
                  data-test="hull-paints-refresh"
                >
                  <ArrowPathIcon className={twMerge("h-5 w-5", loading && "animate-spin")} />
                </button>
              </div>
            </div>

            {error && (
              <div
                className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                role="alert"
                data-test="hull-paints-error"
              >
                {error}
              </div>
            )}

            <div className="grid items-start gap-4 lg:grid-cols-[270px_minmax(0,1fr)]">
              <aside className={twMerge(panelClass, "overflow-hidden lg:sticky lg:top-0")}>
                <HullPaintsFilterPanel
                  filters={filters}
                  vessels={dashboard?.vessels ?? []}
                  onChange={setFilters}
                  onClear={() => setFilters(EMPTY_HULL_PAINTS_FILTERS)}
                />

                <div className="border-t border-gray-200 p-4 dark:border-gray-700">
                  <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Action pane
                  </h2>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setTableMode((current) => (current === "vessels" ? "paints" : "vessels"))}
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-green bg-green px-3 py-2.5 text-xs font-semibold text-white hover:bg-teal-700"
                      data-test="hull-paints-toggle-table"
                    >
                      <EyeIcon className="h-4 w-4" />
                      {tableMode === "vessels" ? "See paint info" : "See vessels"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setExternalAction("log-paint")}
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-green bg-white px-3 py-2.5 text-xs font-semibold text-green hover:bg-primary-10 dark:bg-gray-800"
                      data-test="hull-paints-log-paint"
                    >
                      <BookOpenIcon className="h-4 w-4" />
                      Log new paint in library
                    </button>
                    <button
                      type="button"
                      onClick={openSendRequest}
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-gray-100 px-3 py-2.5 text-xs font-semibold text-gray-700 hover:border-green dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                      data-test="hull-paints-send-request"
                    >
                      <EnvelopeIcon className="h-4 w-4" />
                      Send request to master
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
                  <ClockIcon className="h-5 w-5 flex-none text-green" />
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Snapshot loaded</p>
                    <p className="text-xs font-medium">{lastRefresh}</p>
                  </div>
                </div>
              </aside>

              <section className={twMerge(panelClass, "min-w-0 overflow-hidden")}>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                      {tableMode === "vessels" ? "Running vessel information" : "Hull paint information library"}
                    </h2>
                    <p className="mt-0.5 text-[11px] text-gray-500">
                      {tableMode === "vessels"
                        ? "Select vessel rows to narrow the filtered request recipients."
                        : "Attachment links open in a separate tab."}
                    </p>
                  </div>
                  <p className="rounded-full bg-primary-10 px-3 py-1 text-xs font-semibold text-green">
                    {visibleCount} visible
                  </p>
                </div>
                {loading && !dashboard ? (
                  <div className="p-12 text-center text-sm text-gray-500" data-test="hull-paints-loading">
                    Loading Hull Paints…
                  </div>
                ) : (
                  <HullPaintsDataTable
                    mode={tableMode}
                    vessels={filteredVessels}
                    paints={filteredPaints}
                    selectedImos={selectedImos}
                    onToggleVessel={toggleVessel}
                  />
                )}
              </section>
            </div>
          </div>
        </main>
      </div>

      {externalAction && (
        <HullPaintsExternalActionModal
          action={externalAction}
          mockEnabled={mockEnabled}
          selectedVessels={requestVessels}
          onClose={closeExternalAction}
        />
      )}
    </div>
  );
};

export default HullPaintsInfoActiveVessels;
